<template>
  <base-page-layout class="alteracao-segmentation" :title="title">
    <mf-loading loader="get-segmentation-structure" loader-type="dialog">
      <v-flex v-if="isIndustry" xs4>
        <warning-row>{{ warningIndustry }}</warning-row>
      </v-flex>
      <div class="mb-4" :class="{ 'title-grid': isSegmentationWithoutRfv }">
        <title-description
          id="flyer-title"
          :title.sync="segment.name"
          :description.sync="segment.description"
          :title-error="$v.segment.name.$error"
          :title-error-message="errorMessages.name"
          :description-error="$v.segment.description.$error"
          :description-error-message="errorMessages.description"
          :disable-title="isIndustry"
          :disable-description="isIndustry"
          :is-industry-campaign="isIndustry"
          :tooltip-message="tooltipIndustryMessage"
          mode="segmentation"
        />
        <segmentation-insights
          v-if="isSegmentationWithoutRfv"
          :disable-save-segmentation="!enableSaveSegmentation"
          :save-segmentation="saveSegmentation"
          :loading-save-segmentation="$wait.is(['edit-segment', 'save-segment'])"
          :segment="segment"
          :is-segmentation-without-rfv="isSegmentationWithoutRfv"
        />
      </div>

      <v-row v-if="!isSegmentationWithoutRfv">
        <v-col class="pt-0">
          <segmentation-insights
            :disable-save-segmentation="!enableSaveSegmentation"
            :save-segmentation="saveSegmentation"
            :loading-save-segmentation="$wait.is(['edit-segment', 'save-segment'])"
            :segment="segment"
            :is-segmentation-without-rfv="isSegmentationWithoutRfv"
          />
        </v-col>
      </v-row>

      <v-row no-gutters>
        <v-col v-if="selectedCriteria && !$wait.is('get-segmentation-structure')" cols="3" class="edit-criteria">
          <criterio-details :disabled="isIndustry" :tooltip-message="tooltipIndustryMessage" />
        </v-col>
        <v-col v-if="!selectedCriteria && !$wait.is('get-segmentation-structure')" cols="3" class="edit-criteria">
          <new-criterio :disabled="isIndustry" :title="newCriterioTitle" />
        </v-col>

        <v-col id="criteria-group" cols="9" class="px-6">
          <segmentation-group
            ref="segmentationGroup"
            :disabled="isIndustry"
            :segment="segment"
            :segment-original-size="segmentOriginalSize"
            :is-segmentation-without-rfv="isSegmentationWithoutRfv"
            @filter-type-changed="handleFilterTypeChange"
          />
        </v-col>
      </v-row>

      <div class="floating-actions">
        <mf-action-buttons
          :primary-button="{
            text: 'Salvar segmento',
            action: () => saveSegmentation({ redirect: true }),
            isVisible: true,
            isDisabled: !enableSaveSegmentation || isIndustry,
            isLoading: validatingSegmentPreSave || $wait.is(['edit-segment', 'save-segment']),
            tooltip: !enableSaveSegmentation || isIndustry,
            tooltipMessage: disabledSaveSegmentationTooltip,
          }"
          :cancel-button="{
            text: isIndustry ? 'Voltar' : 'Cancelar',
            action: cancel,
            isVisible: true,
          }"
        />
      </div>
    </mf-loading>
  </base-page-layout>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { mapState, mapGetters, mapActions } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import BasePageLayout from '@/components/BasePageLayout.vue'
import NewCriterio from '@/modules/clientes/segmentation/views/components/NewCriterio.vue'
import SegmentationGroup from '@/modules/clientes/segmentation/views/components/SegmentationGroup.vue'
import CriterioDetails from '@/modules/clientes/segmentation/views/components/CriterioDetails.vue'
import SegmentationInsights from '@/modules/clientes/segmentation/views/components/SegmentationInsights.vue'
import TitleDescription from '@/modules/campanhas/views/components/edit/TitleDescription.vue'
import { required } from 'vuelidate/lib/validators'
import WarningRow from '@/components/WarningRow.vue'

const DEFAULT_EMPTY_GROUPS = { groups: [{ criteria: [], operator: 'AND' }], operator: 'AND' }

export default {
  components: {
    BasePageLayout,
    NewCriterio,
    SegmentationGroup,
    CriterioDetails,
    SegmentationInsights,
    TitleDescription,
    WarningRow,
  },

  props: {
    isNew: {
      type: Boolean,
      default: false,
    },
  },
  data: () => {
    return {
      segment: {
        name: '',
        description: '',
        industry: null,
      },

      errorMessages: {
        name: 'Escolha um nome para este segmento',
        description: 'De uma descrição para este segmento',
      },

      tooltipIndustryMessage: 'Desabilitado devido a pré-configuração da indústria',
      warningIndustry: 'Não é possível fazer alterações na segmentação de ofertas de trade',
      loading: true,
      validatingSegmentPreSave: false,
      newCriterioTitle: 'Critérios da Segmentação',
      initialSegmentOriginalSize: null,
    }
  },
  computed: {
    ...mapGetters({ quickAction: 'getQuickAction' }),

    ...mapState({
      criterions: (state) => state.segmentation.segmentation,
      selectedCriteria: (state) => state.segmentation.selectedCriteria,
      segmentationGroups: (state) => state.segmentation.segmentationGroups,
      segmentationSize: (state) => state.segmentation.segmentationSize,
    }),

    isSegmentationWithoutRfv() {
      const hasRfv = !this.$getLoggedUser().only_mercapromo && this.$permissions.havePagePermission('clientes/rfv')
      return !hasRfv
    },

    disabledSaveSegmentationTooltip() {
      if (this.isIndustry) {
        return this.tooltipIndustryMessage
      }
      if (this.selectedCriteria) {
        return 'Salve ou cancele a edição do critério atual para salvar a segmentação.'
      }
      return ''
    },
    enableSaveSegmentation() {
      return !this.selectedCriteria
    },
    title() {
      return this.loading ? '' : this.isIndustry ? 'Segmentação de trade' : this.isNew ? 'Novo segmento' : 'Editar segmentação'
    },
    isIndustry() {
      return Boolean(this.segment.industry)
    },
    segmentOriginalSize() {
      if (this.segmentationSize?.unlimited_count !== undefined) {
        return this.segmentationSize.unlimited_count
      }
      if (this.segmentationSize?.count !== undefined) {
        return this.segmentationSize.count
      }
      if (this.initialSegmentOriginalSize !== null) {
        return this.initialSegmentOriginalSize
      }
      const fallbackValue = this.segment.originalSize || this.segment.size || 0
      return fallbackValue
    },
  },
  created() {
    this.getData()
  },
  methods: {
    ...mapActions(['setSegmentationGroups', 'unsetQuickAction', 'addCriteriaToLastPosition', 'calculateSegmentSize']),

    ...mapWaitingActions({
      getSegmentationStructure: 'get-segmentation-structure',
      evaluateSegmentation: 'evaluate-segmentation',
      saveSegment: 'save-segment',
      getSegmentById: 'get-segment-by-id',
      editSegment: 'edit-segment',
      getSegmentModelById: 'get-segment-by-id',
    }),

    cancel() {
      if (this.isIndustry) {
        this.$router.push('/clientes/segmentacao/trade').catch(() => {})
        return
      }

      try {
        this.$confirm({
          title: 'Cancelar alterações',
          message: 'Ao confirmar as informações não salvas serão perdidas',
          confirmText: 'Confirmar',
          cancelText: this.isIndustry ? 'Voltar' : 'Cancelar',
        })
          .then(() => {
            this.$router.push('/clientes/segmentacao').catch(() => {})
          })
          .catch(() => {})
      } catch {
        return false
      }
    },

    async saveSegmentation(args) {
      const segmentationGroup = this.$refs.segmentationGroup
      if (segmentationGroup && segmentationGroup.$refs && segmentationGroup.$refs.segmentationLimiter && segmentationGroup.$refs.segmentationLimiter.$v) {
        segmentationGroup.$refs.segmentationLimiter.$v.$touch()
      }
      this.$v.$touch()
      if (
        this.$v.$error ||
        (segmentationGroup &&
          segmentationGroup.$refs &&
          segmentationGroup.$refs.segmentationLimiter &&
          segmentationGroup.$refs.segmentationLimiter.$v &&
          segmentationGroup.$refs.segmentationLimiter.$v.$error)
      ) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        return
      }
      if (!this.segmentationSize) {
        this.validatingSegmentPreSave = true
        try {
          await this.calculateSegmentSize()
        } catch (error) {
          this.$snackbar({ message: 'Segmento inválido', snackbarColor: 'error' })
          this.validatingSegmentPreSave = false
          return
        }
        this.validatingSegmentPreSave = false
      }

      if (this.isNew) {
        return this.createSegment(args)
      } else {
        return this.saveEditSegment(args)
      }
    },

    async createSegment({ redirect = false } = {}) {
      try {
        const response = await this.saveSegment({
          name: this.segment.name,
          description: this.segment.description,
          data: this.segmentationGroups,
        })
        if (redirect) {
          this.$router.push('/clientes/segmentacao').catch(() => {})
        } else {
          return response
        }
      } catch (error) {
        this.$snackbar({ message: 'Segmento inválido', snackbarColor: 'error' })
      }
    },

    async saveEditSegment({ redirect = false } = {}) {
      try {
        const response = await this.editSegment({
          id: this.$route.params.id,
          name: this.segment.name,
          description: this.segment.description,
          data: this.segmentationGroups,
        })
        if (redirect) {
          this.$router.push('/clientes/segmentacao').catch(() => {})
        } else {
          return response
        }
      } catch (error) {
        this.$snackbar({ message: 'Segmento inválido', snackbarColor: 'error' })
      }
    },
    loadPrebuiltSegmentation() {
      if (!this.getIsQuickAction() || !this.quickAction?.segmentation) {
        return
      }

      this.setSegmentationGroups(this.quickAction.segmentation.segment)

      if (this.isNew) {
        this.segment.name = this.quickAction.segmentation.title
        this.segment.description = this.quickAction.segmentation.description
      }

      this.unsetQuickAction()
    },
    async getInitialGroups() {
      if (this.isNew) {
        const id = this.$route.params.id
        if (id) {
          await this.getSegmentModelById(id).then((model) => {
            this.setSegmentationGroups(model.data)
            this.segment.name = model.name
            this.segment.description = model.description
            this.segment.size = model.clients_qty
            this.initialSegmentOriginalSize = model.clients_qty
          })
        } else {
          this.setSegmentationGroups(cloneDeep(DEFAULT_EMPTY_GROUPS))
        }
      } else {
        await this.getSegmentById(this.$route.params.id).then((response) => {
          this.setSegmentationGroups(response.data)
          this.segment.name = response.name
          this.segment.description = response.description
          this.segment.industry = response.industry
          this.segment.size = response.clients_qty
          this.segment.originalSize = response.unlimited_clients_qty
          this.initialSegmentOriginalSize = response.unlimited_clients_qty || response.clients_qty
        })
      }
    },
    async getData() {
      this.loading = true
      const modules = Object.entries(this.$getAllowedModules())
        .filter(([_, value]) => value)
        .map(([key]) => key)

      await Promise.all([this.getSegmentationStructure(modules), this.getInitialGroups()])
      this.$nextTick(() => {
        this.loadPrebuiltSegmentation()
        this.evaluateSegmentation({ segment: this.segmentationGroups })
      })
      this.loading = false
    },

    isEditSegment() {
      return Boolean(this.$route.params.id)
    },
    handleFilterTypeChange(newFilterType) {
      const type = newFilterType === 'group' ? 'Critérios' : 'Mapa'
      this.newCriterioTitle = `${type} da Segmentação`
    },
  },
  validations: {
    segment: {
      name: { required },
      description: { required },
    },
  },
}
</script>

<style lang="scss" scoped>
.title-grid {
  display: grid;
  grid-template-columns: 1fr 240px;
  gap: 12px;
  align-items: start;
}
.alteracao-segmentation {
  overflow-x: hidden;
  .buttons-container {
    padding: 0 24px;
  }

  .space-between {
    display: flex;
    flex-direction: column;
  }
}
.floating-actions {
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 6;
  background-color: transparent;
  padding: 16px;
}

.floating-actions ::v-deep .v-btn.v-btn--outlined.vermelho-d20--text {
  background-color: white !important;
}
</style>
